
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/card";
import { Badge } from "@/shared/components/badge";
import { StatusTag } from "@/shared/components/StatusTag";
import { Button } from "@/shared/components/button";
import { Progress } from "@/shared/components/misc";
import { Icon } from "@iconify/react";
import { Segmented, Skeleton } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { NotAvailable } from "@/shared/components/NotAvailable";
import type {
  EncounterRecord as Encounter,
  ClinicalNoteRecord as ClinicalNote,
  TreatmentPlanRecord as TreatmentPlan,
  CareProviderRecord as CareProvider,
} from "@/features/patient/care-treatment/utils/adaptCareTreatment";
import { cn } from "@/shared/lib/utils";
import { PageBackButton } from "@/shared/components/PageBackButton";

interface CareTreatmentSectionProps {
  encounters: Encounter[];
  notes: ClinicalNote[];
  plans: TreatmentPlan[];
  providers: CareProvider[];
  isLoading?: boolean;
}

function goalProgressAverage(goals: TreatmentPlan["goals"]): number | null {
  const tracked = goals.map((g) => g.progress).filter((p): p is number => typeof p === "number");
  if (tracked.length === 0) return null;
  return Math.round(tracked.reduce((sum, p) => sum + p, 0) / tracked.length);
}

// Provider role config
const providerRoleConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  primary_care: { label: "Primary Care", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  specialist: { label: "Specialist", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  surgeon: { label: "Surgeon", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  therapist: { label: "Therapist", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  dentist: { label: "Dentist", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  optometrist: { label: "Optometrist", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  other: { label: "Other", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
};

// Encounter type config
const encounterTypeConfig: Record<string, { icon: string; label: string; color: string; bgColor: string }> = {
  office_visit: { icon: "lucide:stethoscope", label: "Office Visit", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  telehealth: { icon: "lucide:video", label: "Telehealth", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  emergency: { icon: "lucide:ambulance", label: "Emergency", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  inpatient: { icon: "lucide:building-2", label: "Inpatient", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  outpatient: { icon: "lucide:building-2", label: "Outpatient", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  home_visit: { icon: "lucide:home", label: "Home Visit", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  lab_only: { icon: "lucide:test-tube", label: "Lab Visit", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
};

// Note type config
const noteTypeConfig: Record<string, { icon: string; label: string; color: string; bgColor: string }> = {
  progress_note: { icon: "lucide:clipboard-list", label: "Progress Note", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  consultation: { icon: "lucide:message-square", label: "Consultation", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  discharge_summary: { icon: "lucide:file-check", label: "Discharge Summary", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  procedure_note: { icon: "lucide:stethoscope", label: "Procedure Note", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  nursing_note: { icon: "lucide:scroll-text", label: "Nursing Note", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
  referral: { icon: "lucide:send", label: "Referral", color: "!text-white", bgColor: "bg-[#E4002B]/50" },
};

// Plan status styles
const planStatusLabels: Record<string, string> = {
  active: "Active",
  completed: "Completed",
  discontinued: "Discontinued",
};

const goalStatusLabels: Record<string, string> = {
  not_started: "Not Started",
  in_progress: "In Progress",
  achieved: "Achieved",
  modified: "Modified",
};

const redIconBoxClass = "flex shrink-0 items-center justify-center rounded-2xl bg-[#E4002B]/50";
const redTagClass = "bg-[#E4002B]/50 !text-white";
const concernLabelClass = "rounded-md bg-[#E4002B]/50 px-2 py-0.5 text-[11px] font-semibold !text-white";
const warningClass = "mb-4 rounded-2xl border border-[#E5E7EB] bg-[#F3F4F6] p-4 text-sm font-medium text-[#6B7280]";
const filterButtonClass = "!h-7 !rounded-full !px-3 !text-[10px] !font-bold";
const activeFilterClass = "!border-[#F36F8F] !bg-[#F36F8F] !text-white hover:!border-[#F36F8F] hover:!bg-[#F36F8F] hover:!text-white";
const inactiveFilterClass = "!border-[#E5E7EB] !bg-[#F3F4F6] !text-[#111827] hover:!border-[#D1D5DB] hover:!bg-white hover:!text-[#111827]";

function formatDate(dateString: string): string {
  if (!dateString) return "Not available";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const date = new Date(dateString + "T00:00:00");
  if (Number.isNaN(date.getTime())) return "Not available";
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export function CareTreatmentSection({ encounters, notes, plans, providers, isLoading = false }: CareTreatmentSectionProps) {
  const [activeTab, setActiveTab] = useState("encounters");
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [selectedNote, setSelectedNote] = useState<ClinicalNote | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<TreatmentPlan | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<CareProvider | null>(null);
  const [encounterFilter, setEncounterFilter] = useState<string>("all");
  const [noteFilter, setNoteFilter] = useState<string>("all");

  const activePlans = plans.filter(p => p.status === "active");
  const sortedEncounters = [...encounters].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const sortedNotes = [...notes].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const sortedProviders = [...providers].sort((a, b) => a.name.localeCompare(b.name));

  if (isLoading) {
    return (
      <div className="min-h-full bg-[#F7F7F7] p-6">
        <div className="mb-5 grid gap-5 sm:grid-cols-5">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="min-h-[110px] rounded-3xl border border-[#E5E7EB] bg-white px-6 py-5 shadow-sm">
              <Skeleton active paragraph={{ rows: 1 }} />
            </div>
          ))}
        </div>
        <div className="grid gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
              <Skeleton active avatar paragraph={{ rows: 2 }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Encounter detail view
  if (selectedEncounter) {
    const type = encounterTypeConfig[selectedEncounter.type];

    return (
      <div className="p-6">
        <PageBackButton onClick={() => setSelectedEncounter(null)} ariaLabel="Back to Care and Treatment" className="!mb-8" />

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={cn("flex h-14 w-14 items-center justify-center rounded-xl", type.bgColor)}>
                  <Icon icon={type.icon} className={cn("h-7 w-7", type.color)} />
                </div>
                <div>
                  <span className={concernLabelClass}>
                    {type.label}
                  </span>
                  <CardTitle className="mt-2 text-xl">{selectedEncounter.reasonForVisit}</CardTitle>
                  <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon icon="lucide:calendar-days" className="h-4 w-4" />
                    {formatDate(selectedEncounter.date)}{selectedEncounter.time ? ` at ${selectedEncounter.time}` : ""}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon icon="lucide:user" className="h-4 w-4" />
                  <span className="text-sm">Provider</span>
                </div>
                <p className="mt-1 font-medium">{selectedEncounter.provider || <NotAvailable />}</p>
                <p className="text-sm text-muted-foreground">{selectedEncounter.providerSpecialty || <NotAvailable />}</p>
              </div>
              <div className="rounded-lg bg-muted/50 p-4">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon icon="lucide:building-2" className="h-4 w-4" />
                  <span className="text-sm">Facility</span>
                </div>
                <p className="mt-1 font-medium">{selectedEncounter.facility || <NotAvailable />}</p>
              </div>
            </div>

            {selectedEncounter.chiefComplaint && (
              <div>
                <h3 className="mb-2 font-semibold text-foreground">Chief Complaint</h3>
                <p className="text-muted-foreground">{selectedEncounter.chiefComplaint}</p>
              </div>
            )}

            {selectedEncounter.diagnosis && selectedEncounter.diagnosis.length > 0 && (
              <div>
                <h3 className="mb-2 font-semibold text-foreground">Diagnoses</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedEncounter.diagnosis.map((dx, idx) => (
                    <span key={idx} className={concernLabelClass}>{dx}</span>
                  ))}
                </div>
              </div>
            )}

            {selectedEncounter.procedures && selectedEncounter.procedures.length > 0 && (
              <div>
                <h3 className="mb-2 font-semibold text-foreground">Procedures / Tests</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedEncounter.procedures.map((proc, idx) => (
                    <span key={idx} className={concernLabelClass}>{proc}</span>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="mb-2 font-semibold text-foreground">Notes</h3>
              <div className="rounded-lg border border-border bg-card p-4">
                <p className="text-sm text-foreground">{selectedEncounter.notes || <NotAvailable label="No visit notes were included with this record." />}</p>
              </div>
            </div>

            {selectedEncounter.followUp && (
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="flex items-center gap-2">
                  <Icon icon="lucide:clock" className="h-5 w-5 text-[#9CA3AF]" />
                  <h3 className="font-semibold text-foreground">Follow-Up</h3>
                </div>
                <p className="mt-2 text-sm text-foreground">{selectedEncounter.followUp}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Clinical Note detail view
  if (selectedNote) {
    const type = noteTypeConfig[selectedNote.type];

    return (
      <div className="p-6">
        <PageBackButton onClick={() => setSelectedNote(null)} ariaLabel="Back to Care and Treatment" className="!mb-8" />

        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className={cn("flex h-14 w-14 items-center justify-center rounded-xl", type.bgColor)}>
                  <Icon icon={type.icon} className={cn("h-7 w-7", type.color)} />
                </div>
                <div>
                  <span className={concernLabelClass}>
                    {type.label}
                  </span>
                  <CardTitle className="mt-2 text-xl">{selectedNote.specialty || noteTypeConfig[selectedNote.type].label} Note</CardTitle>
                  <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon icon="lucide:calendar" className="h-4 w-4" />
                    {formatDate(selectedNote.date)}
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="rounded-lg bg-muted/50 p-4">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Icon icon="lucide:user" className="h-4 w-4" />
                <span className="text-sm">Provider</span>
              </div>
              <p className="mt-1 font-medium">
                {selectedNote.provider
                  ? `${selectedNote.provider}${selectedNote.providerCredentials ? `, ${selectedNote.providerCredentials}` : ""}`
                  : <NotAvailable />}
              </p>
              <p className="text-sm text-muted-foreground">{selectedNote.specialty || <NotAvailable />}</p>
            </div>

            {(selectedNote.subjective || selectedNote.objective || selectedNote.assessment || selectedNote.plan) && (
              <div className="space-y-4">
                {selectedNote.subjective && (
                  <div>
                    <h3 className="mb-2 font-semibold text-foreground">Subjective</h3>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-sm text-foreground whitespace-pre-wrap">{selectedNote.subjective}</p>
                    </div>
                  </div>
                )}
                {selectedNote.objective && (
                  <div>
                    <h3 className="mb-2 font-semibold text-foreground">Objective</h3>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-sm text-foreground whitespace-pre-wrap">{selectedNote.objective}</p>
                    </div>
                  </div>
                )}
                {selectedNote.assessment && (
                  <div>
                    <h3 className="mb-2 font-semibold text-foreground">Assessment</h3>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-sm text-foreground whitespace-pre-wrap">{selectedNote.assessment}</p>
                    </div>
                  </div>
                )}
                {selectedNote.plan && (
                  <div>
                    <h3 className="mb-2 font-semibold text-foreground">Plan</h3>
                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <p className="text-sm text-foreground whitespace-pre-wrap">{selectedNote.plan}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!selectedNote.subjective && (
              <div>
                <h3 className="mb-2 font-semibold text-foreground">Summary</h3>
                <div className="rounded-lg border border-border bg-card p-4">
                  <p className="text-sm text-foreground">
                    {selectedNote.content || (
                      <NotAvailable
                        label={
                          selectedNote.hasExtractedContent
                            ? "The full note text is stored as a document and isn't shown here yet."
                            : "No note text was included with this record."
                        }
                      />
                    )}
                  </p>
                </div>
              </div>
            )}

            {selectedNote.diagnoses && selectedNote.diagnoses.length > 0 && (
              <div>
                <h3 className="mb-2 font-semibold text-foreground">Diagnoses</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedNote.diagnoses.map((dx, idx) => (
                    <span key={idx} className={concernLabelClass}>{dx}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="border-t border-border pt-4">
              <p className="text-sm text-muted-foreground">
                {selectedNote.provider
                  ? `Electronically signed by ${selectedNote.provider}${selectedNote.providerCredentials ? `, ${selectedNote.providerCredentials}` : ""} on ${formatDate(selectedNote.signedDate)}`
                  : `Recorded ${formatDate(selectedNote.signedDate)}`}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Treatment Plan detail view
  if (selectedPlan) {
    const statusLabel = planStatusLabels[selectedPlan.status];
    const overallProgress = goalProgressAverage(selectedPlan.goals);

    return (
      <div className="p-6 lg:p-8">
        <PageBackButton onClick={() => setSelectedPlan(null)} ariaLabel="Back to Care and Treatment" className="!mb-8" />

        <div className="mb-8">
          <div className="flex items-start gap-4">
            <div className={cn(redIconBoxClass, "h-14 w-14")}>
              <Icon icon="lucide:clipboard-list" className="h-7 w-7 !text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-semibold text-foreground">{selectedPlan.diagnosisName}</h1>
                <span className={concernLabelClass}>{statusLabel}</span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {selectedPlan.createdBy
                  ? `Treatment plan created by ${selectedPlan.createdBy} on ${formatDate(selectedPlan.createdDate)}`
                  : "Treatment plan from your records"}
              </p>
            </div>
          </div>

          <Card className="mt-6">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="font-medium">Overall Progress</p>
                {overallProgress != null ? (
                  <span className="text-2xl font-bold text-[#0A2947]">{overallProgress}%</span>
                ) : (
                  <NotAvailable label="Not tracked" />
                )}
              </div>
              {overallProgress != null ? (
                <Progress value={overallProgress} className="h-3" strokeColor="#0A2947" />
              ) : (
                <div className="h-3 w-full rounded-full bg-[#F3F4F6]" />
              )}
              <p className="mt-2 text-sm text-muted-foreground">
                Next review:{" "}
                {selectedPlan.nextReviewDate ? formatDate(selectedPlan.nextReviewDate) : <NotAvailable />}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon icon="lucide:target" className="h-5 w-5 text-[#0A2947]" />
                Treatment Goals
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedPlan.goals.length === 0 ? (
                <EmptyState
                  icon={<Icon icon="lucide:target" className="h-12 w-12 text-[#9CA3AF]" />}
                  title="No treatment goals"
                  description="No treatment goals were included with this plan."
                />
              ) : (
                selectedPlan.goals.map((goal) => (
                  <div key={goal.id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-sm font-medium">{goal.description}</p>
                      <span className={cn("shrink-0", concernLabelClass)}>{goalStatusLabels[goal.status]}</span>
                    </div>
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                        <span>Progress</span>
                        {typeof goal.progress === "number" ? <span>{goal.progress}%</span> : <NotAvailable label="Not tracked" />}
                      </div>
                      {typeof goal.progress === "number" ? (
                        <Progress value={goal.progress} className="h-2" strokeColor="#0A2947" />
                      ) : (
                        <div className="h-2 w-full rounded-full bg-[#F3F4F6]" />
                      )}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Target: {goal.targetDate ? formatDate(goal.targetDate) : "Not available"}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon icon="lucide:pill" className="h-5 w-5 text-[#0A2947]" />
                Medications
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <EmptyState
                icon={<Icon icon="lucide:pill" className="h-12 w-12 text-[#9CA3AF]" />}
                title="No medications listed"
                description="This care plan doesn't list medications. See the Medications page for your full list."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon icon="lucide:calendar" className="h-5 w-5 text-[#0A2947]" />
                Scheduled Care
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <EmptyState
                icon={<Icon icon="lucide:calendar" className="h-12 w-12 text-[#9CA3AF]" />}
                title="No scheduled care"
                description="No scheduled care was included with this plan."
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon icon="lucide:heart" className="h-5 w-5 text-[#0A2947]" />
                Lifestyle Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedPlan.lifestyle.length === 0 ? (
                <EmptyState
                  icon={<Icon icon="lucide:heart" className="h-12 w-12 text-[#9CA3AF]" />}
                  title="No lifestyle recommendations"
                  description="No lifestyle recommendations were included with this plan."
                />
              ) : (
                <ul className="space-y-3">
                  {selectedPlan.lifestyle.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Icon icon="lucide:check-circle-2" className="mt-0.5 h-4 w-4 shrink-0 text-[#0A2947]" />
                      <span className="text-sm text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {selectedPlan.notes && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-base">Provider Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">{selectedPlan.notes}</p>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Provider detail view
  if (selectedProvider) {
    const role = providerRoleConfig[selectedProvider.role];

    return (
      <div className="p-6">
        <PageBackButton onClick={() => setSelectedProvider(null)} ariaLabel="Back to Care and Treatment" className="!mb-8" />

        <Card className="rounded-3xl border-[#E5E7EB] bg-white shadow-sm">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className={cn(redIconBoxClass, "h-16 w-16")}>
                  <Icon icon="lucide:user" className="h-8 w-8 !text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="text-xl font-extrabold text-[#0A2947]">{selectedProvider.name}</CardTitle>
                    <span className={concernLabelClass}>
                      {role.label}
                    </span>
                  </div>
                  <p className="mt-1 text-sm font-semibold text-[#9CA3AF]">{selectedProvider.credentials || <NotAvailable />}</p>
                  <p className="font-extrabold text-[#0A2947]">{selectedProvider.specialty || <NotAvailable label="Specialty not available" />}</p>
                  {selectedProvider.subSpecialty && (
                    <p className="text-sm font-medium text-[#6B7280]">{selectedProvider.subSpecialty}</p>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-2xl bg-[#F3F4F6] p-4">
                <div className="flex items-center gap-2 text-[#9CA3AF]">
                  <Icon icon="lucide:calendar-days" className="h-4 w-4 text-[#9CA3AF]" />
                  <span className="text-sm">Last Seen</span>
                </div>
                <p className="mt-1 font-bold text-[#0A2947]">
                  {selectedProvider.lastSeenDate ? formatDate(selectedProvider.lastSeenDate) : <NotAvailable />}
                </p>
              </div>
              {selectedProvider.nextAppointment && (
                <div className="rounded-2xl border border-[#E5E7EB] bg-[#F3F4F6] p-4">
                  <div className="flex items-center gap-2 text-[#9CA3AF]">
                    <Icon icon="lucide:calendar" className="h-4 w-4 text-[#9CA3AF]" />
                    <span className="text-sm">Next Appointment</span>
                  </div>
                  <p className="mt-1 font-bold text-[#0A2947]">{formatDate(selectedProvider.nextAppointment)}</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="mb-3 font-extrabold text-[#0A2947]">Contact Information</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-2xl bg-[#F3F4F6] p-3">
                  <Icon icon="lucide:building-2" className="h-5 w-5 text-[#9CA3AF]" />
                  <span className="text-sm font-medium text-[#6B7280]">{selectedProvider.facility || <NotAvailable />}</span>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-[#F3F4F6] p-3">
                  <Icon icon="lucide:map-pin" className="h-5 w-5 text-[#9CA3AF]" />
                  <span className="text-sm font-medium text-[#6B7280]">{selectedProvider.address || <NotAvailable />}</span>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-[#F3F4F6] p-3">
                  <Icon icon="lucide:phone" className="h-5 w-5 text-[#9CA3AF]" />
                  <span className="text-sm font-medium text-[#6B7280]">{selectedProvider.phone || <NotAvailable />}</span>
                </div>
                {selectedProvider.email && (
                  <div className="flex items-center gap-3 rounded-2xl bg-[#F3F4F6] p-3">
                    <Icon icon="lucide:mail" className="h-5 w-5 text-[#9CA3AF]" />
                    <span className="text-sm font-medium text-[#6B7280]">{selectedProvider.email}</span>
                  </div>
                )}
              </div>
            </div>

            {selectedProvider.notes && (
              <div>
                <h3 className="mb-2 font-extrabold text-[#0A2947]">Notes</h3>
                <div className="rounded-2xl border border-[#E5E7EB] bg-white p-4">
                  <p className="text-sm font-medium text-[#6B7280]">{selectedProvider.notes}</p>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              {selectedProvider.npi && (
                <span className={concernLabelClass}>NPI: {selectedProvider.npi}</span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main view with tabs
  return (
    <div className="min-h-full bg-[#F7F7F7] p-6">
      
      {/* Summary Stats */}
      <div className="mb-5 grid gap-5 sm:grid-cols-5">
        {[
          { icon: "lucide:users", label: "Care Team", value: providers.length, tab: "providers" },
          { icon: "lucide:calendar-days", label: "Encounters", value: encounters.length, tab: "encounters" },
          { icon: "lucide:file-text", label: "Clinical Notes", value: notes.length, tab: "notes" },
          { icon: "lucide:clipboard-list", label: "Active Plans", value: activePlans.length, tab: "plans" },
          { icon: "lucide:target", label: "Active Goals", value: activePlans.reduce((sum, p) => sum + p.goals.filter(g => g.status !== "achieved").length, 0), tab: "plans" },
        ].map(({ icon, label, value, tab }) => (
          <Button
            key={label}
            variant="outline"
            onClick={() => setActiveTab(tab)}
            className="!flex !h-auto !min-h-[110px] !cursor-pointer !flex-col !items-stretch !justify-between !rounded-3xl !border-[#E5E7EB] !bg-white !px-6 !py-5 !text-left !shadow-sm hover:!-translate-y-0.5 hover:!border-[#E5E7EB] hover:!bg-white hover:!shadow-lg"
          >
            <div className="flex items-center gap-2">
              <div className={cn(redIconBoxClass, "h-9 w-9 rounded-full")}>
                <Icon icon={icon} className="h-4 w-4 !text-white" />
              </div>
              <p className="text-sm font-semibold text-[#111827]">{label}</p>
            </div>
            <p className="text-right text-3xl font-bold text-[#111827]">{value}</p>
          </Button>
        ))}
      </div>

      {/* Tabs */}
      <div className="mb-6 flex justify-center">
        <Segmented
          value={activeTab}
          onChange={(v) => setActiveTab(v as string)}
          options={[
            { key: "providers", icon: "lucide:users", label: "Providers" },
            { key: "encounters", icon: "lucide:calendar-days", label: "Encounters" },
            { key: "notes", icon: "lucide:file-text", label: "Notes" },
            { key: "plans", icon: "lucide:clipboard-list", label: "Plans" },
          ].map(({ key, icon, label }) => ({
            value: key,
            label: (
              <span className="flex items-center gap-2 px-5 py-1 text-sm font-extrabold">
                <Icon icon={icon} className="h-4 w-4" />
                {label}
              </span>
            ),
          }))}
          className="!rounded-full !border-2 !border-[#9CA3AF] !bg-white !p-1 !shadow-sm [&_.ant-segmented-item]:!rounded-full [&_.ant-segmented-item-selected]:!bg-[#0A2947] [&_.ant-segmented-item-selected]:!shadow-sm [&_.ant-segmented-item-selected_span]:!text-white [&_.ant-segmented-item:not(.ant-segmented-item-selected)_span]:!text-[#0A2947] [&_.ant-segmented-thumb]:!rounded-full"
        />
      </div>

        {/* Care Team Tab */}
        {activeTab === "providers" && (
        <div className="grid gap-4">
          <div className={warningClass}>
            <p>
              <strong>Your Care Team:</strong> These are the healthcare providers involved in your care.
              Click on any provider to see their contact information and appointment history.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {sortedProviders.map((provider) => {
              const role = providerRoleConfig[provider.role];
              return (
                <Card
                  key={provider.id}
                  className="cursor-pointer rounded-3xl border-[#E5E7EB] bg-white shadow-sm transition-all hover:bg-[#FFF8F8] hover:shadow-md"
                  onClick={() => setSelectedProvider(provider)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={cn(redIconBoxClass, "h-12 w-12")}>
                        <Icon icon="lucide:user" className="h-6 w-6 !text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="truncate font-extrabold text-[#0A2947]">{provider.name}</h3>
                          <span className={cn("shrink-0", concernLabelClass)}>
                            {role.label}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-[#0A2947]">{provider.specialty || <NotAvailable label="Specialty not available" />}</p>
                        <p className="mt-1 truncate text-sm text-[#9CA3AF]">{provider.facility || <NotAvailable />}</p>

                        {provider.lastSeenDate && (
                          <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Icon icon="lucide:calendar-days" className="h-3.5 w-3.5" />
                              Last seen: {formatDate(provider.lastSeenDate)}
                            </span>
                          </div>
                        )}

                        {provider.nextAppointment && (
                          <div className="mt-1.5 flex items-center gap-1 text-xs text-muted-foreground">
                            <Icon icon="lucide:calendar" className="h-3.5 w-3.5" />
                            Next: {formatDate(provider.nextAppointment)}
                          </div>
                        )}
                      </div>
                      <Icon icon="lucide:chevron-right" className="h-5 w-5 shrink-0 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {providers.length === 0 && (
            <EmptyState
              icon={<Icon icon="lucide:users" className="h-12 w-12 text-[#9CA3AF]" />}
              title="No care providers"
              description="Your healthcare providers will appear here"
            />
          )}
        </div>
        )}

        {/* Encounters Tab */}
        {activeTab === "encounters" && (
        <div className="space-y-4">
          <div className="mb-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              className={cn(filterButtonClass, encounterFilter === "all" ? activeFilterClass : inactiveFilterClass)}
              onClick={() => setEncounterFilter("all")}
            >
              All
            </Button>
            {[...new Set(encounters.map((e) => e.type))].map((type) => {
              const config = encounterTypeConfig[type];
              return (
                <Button
                  key={type}
                  variant="outline"
                  className={cn(filterButtonClass, encounterFilter === type ? activeFilterClass : inactiveFilterClass)}
                  onClick={() => setEncounterFilter(type)}
                >
                  {config.label}
                </Button>
              );
            })}
          </div>

          <div className="grid gap-4">
            {(encounterFilter === "all" ? sortedEncounters : sortedEncounters.filter(e => e.type === encounterFilter)).map((encounter) => {
              const type = encounterTypeConfig[encounter.type];
              return (
                <Card
                  key={encounter.id}
                  className="cursor-pointer rounded-3xl border-[#E5E7EB] bg-white shadow-sm transition-all hover:bg-[#FFF8F8] hover:shadow-md"
                  onClick={() => setSelectedEncounter(encounter)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl", type.bgColor)}>
                        <Icon icon={type.icon} className={cn("h-6 w-6", type.color)} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-extrabold text-[#0A2947]">{encounter.reasonForVisit}</h3>
                          <span className={concernLabelClass}>{type.label}</span>
                        </div>
                        <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
                          <span>{encounter.provider || <NotAvailable />}</span>
                          <span>-</span>
                          <span>{encounter.facility || <NotAvailable />}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
                          <Icon icon="lucide:calendar-days" className="h-3 w-3" />
                          {formatDate(encounter.date)}{encounter.time ? ` at ${encounter.time}` : ""}
                        </div>
                      </div>
                      <Icon icon="lucide:chevron-right" className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        )}

        {/* Clinical Notes Tab */}
        {activeTab === "notes" && (
        <div className="grid gap-4">
          <div className={warningClass}>
            <p>
              <strong>Understanding Clinical Notes:</strong> These are detailed records written by your healthcare providers.
              Many notes use the SOAP format: Subjective (your symptoms), Objective (exam findings), Assessment (diagnosis), 
              and Plan (next steps).
            </p>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            <Button
              variant="outline"
              className={cn(filterButtonClass, noteFilter === "all" ? activeFilterClass : inactiveFilterClass)}
              onClick={() => setNoteFilter("all")}
            >
              All ({notes.length})
            </Button>
            {[...new Set(notes.map((n) => n.type))].map((type) => {
              const config = noteTypeConfig[type];
              const count = notes.filter((n) => n.type === type).length;
              return (
                <Button
                  key={type}
                  variant="outline"
                  className={cn(filterButtonClass, noteFilter === type ? activeFilterClass : inactiveFilterClass)}
                  onClick={() => setNoteFilter(type)}
                >
                  {config.label} ({count})
                </Button>
              );
            })}
          </div>

          <div className="grid gap-4">
            {(noteFilter === "all" ? sortedNotes : sortedNotes.filter(n => n.type === noteFilter)).map((note) => {
              const type = noteTypeConfig[note.type];
              return (
                <Card
                  key={note.id}
                  className="cursor-pointer rounded-3xl border-[#E5E7EB] bg-white shadow-sm transition-all hover:bg-[#FFF8F8] hover:shadow-md"
                  onClick={() => setSelectedNote(note)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className={cn("flex h-12 w-12 items-center justify-center rounded-xl", type.bgColor)}>
                        <Icon icon={type.icon} className={cn("h-6 w-6", type.color)} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-extrabold text-[#0A2947]">{note.specialty} - {type.label}</h3>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                          <Icon icon="lucide:user" className="h-3 w-3" />
                          <span>
                            {note.provider
                              ? `${note.provider}${note.providerCredentials ? `, ${note.providerCredentials}` : ""}`
                              : <NotAvailable />}
                          </span>
                        </div>
                        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">
                          {note.content || (
                            <NotAvailable
                              label={note.hasExtractedContent ? "Full text stored as a document" : "No note text on file"}
                            />
                          )}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="flex items-center gap-1 text-sm text-muted-foreground">
                          <Icon icon="lucide:calendar" className="h-3 w-3" />
                          {formatDate(note.date)}
                        </div>
                        <Icon icon="lucide:chevron-right" className="h-5 w-5 text-muted-foreground" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
        )}

        {/* Treatment Plans Tab */}
        {activeTab === "plans" && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {plans.map((plan) => {
              const progress = goalProgressAverage(plan.goals);

              return (
                <Card
                  key={plan.id}
                  className="cursor-pointer rounded-3xl border-[#E5E7EB] bg-white shadow-sm transition-all hover:border-[#F36F8F]/50 hover:shadow-md"
                  onClick={() => setSelectedPlan(plan)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <div className={cn(redIconBoxClass, "h-12 w-12")}>
                        <Icon icon="lucide:clipboard-list" className="h-6 w-6 !text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="truncate font-extrabold text-[#0A2947]">{plan.diagnosisName}</h3>
                          <span className={cn("shrink-0", concernLabelClass)}>{planStatusLabels[plan.status]}</span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{plan.createdBy || <NotAvailable label="Provider not recorded" />}</p>

                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>{plan.goals.length} goal{plan.goals.length === 1 ? "" : "s"}</span>
                            {progress != null ? <span>{progress}% complete</span> : <NotAvailable label="Progress not tracked" />}
                          </div>
                          {progress != null ? (
                            <Progress value={progress} className="h-2" strokeColor="#0A2947" />
                          ) : (
                            <div className="h-2 w-full rounded-full bg-[#F3F4F6]" />
                          )}
                        </div>

                        <div className="mt-3 flex gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Icon icon="lucide:calendar" className="h-3.5 w-3.5" />
                            Review: {plan.nextReviewDate ? formatDate(plan.nextReviewDate) : "Not available"}
                          </span>
                        </div>
                      </div>
                      <Icon icon="lucide:chevron-right" className="h-5 w-5 shrink-0 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {plans.length === 0 && (
            <EmptyState
              icon={<Icon icon="lucide:clipboard-list" className="h-12 w-12 text-[#9CA3AF]" />}
              title="No treatment plans"
              description="Treatment plans from your providers will appear here"
            />
          )}
        </div>
        )}
    </div>
  );
}
